// ============================================
// FAQ 登录/注册弹窗 — 简洁版
// 复用 auth-store 的 signIn 接口，注册/OTP 直接调用 Supabase Auth
// 支持：内部/外部邮箱、密码登录 / OTP 验证码登录 / 企业微信扫码登录
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Mail, Lock, Eye, EyeOff, AlertCircle,
  LogIn, UserPlus, CheckCircle2, KeyRound, Send, Loader2,
  ArrowLeft, Building2,
} from 'lucide-react';
import * as ww from '@wecom/jssdk';
import { useAuthStore } from '../stores/auth-store';
import { useUserStore } from '../stores/user-store';
import { supabase } from '../config/supabase';
import Captcha, { CaptchaHandle } from '../components/auth/Captcha';
import { getFaqAuthRedirectUrl } from './faq-auth-redirect';
import { useOptionalFaqSpace } from './FaqSpaceContext';

interface FaqAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthTab = 'login' | 'register';
type EmailMode = 'internal' | 'external';
type WeComFallback = {
  userid?: string;
  detail?: string;
};

const EMAIL_DOMAIN = '@unilumin.com';
const WECOM_UNSUPPORTED_TITLE = '当前账号暂不支持企业微信登录';
const WECOM_UNSUPPORTED_DETAIL = '系统没有获取到可匹配的企业邮箱，请改用邮箱密码登录；如果还没有账号，可以先注册。';

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function getAuthErrorDetails(error: unknown) {
  const maybeError = error as { message?: unknown; status?: unknown; code?: unknown };
  const message = typeof maybeError.message === 'string' ? maybeError.message : '';
  const status = typeof maybeError.status === 'number' ? maybeError.status : undefined;
  const code = typeof maybeError.code === 'string' ? maybeError.code : '';
  return { message, status, code, normalized: `${message} ${code}`.toLowerCase() };
}

function formatEmailDeliveryFailure(action: '注册' | '发送验证码', emailMode: EmailMode) {
  const suffix = emailMode === 'internal'
    ? '请检查内部邮箱前缀是否正确，确认该邮箱真实存在。'
    : '请检查邮箱地址是否拼写正确，确认该邮箱真实存在。';
  return `${action}失败：邮件服务器拒收或确认邮件发送失败。${suffix}`;
}

function formatAuthRequestError(error: unknown, action: '注册' | '发送验证码', emailMode: EmailMode) {
  const { message, status, normalized } = getAuthErrorDetails(error);

  if (
    normalized.includes('already registered') ||
    normalized.includes('user already registered') ||
    normalized.includes('email_exists') ||
    normalized.includes('已被注册')
  ) {
    return '该邮箱已被注册，请直接登录或使用验证码登录';
  }

  if (
    normalized.includes('error sending') ||
    normalized.includes('confirmation email') ||
    normalized.includes('magic link') ||
    normalized.includes('email provider') ||
    normalized.includes('smtp') ||
    (status === 500 && normalized.includes('email'))
  ) {
    return formatEmailDeliveryFailure(action, emailMode);
  }

  if (normalized.includes('rate limit') || normalized.includes('over_email_send_rate_limit') || normalized.includes('too many')) {
    return '请求过于频繁，请稍后再试';
  }

  if (normalized.includes('invalid email')) {
    return '邮箱格式无效，请检查后重试';
  }

  if (normalized.includes('password')) {
    return '密码不符合安全要求，请提高复杂度后重试';
  }

  if (message) return `${action}失败：${message}`;
  return `${action}失败，请稍后再试`;
}

export default function FaqAuthModal({ isOpen, onClose }: FaqAuthModalProps) {
  const { signIn } = useAuthStore();
  const { fetchProfile } = useUserStore();
  const faqSpaceContext = useOptionalFaqSpace();
  const faqBasePath = faqSpaceContext?.basePath ?? '/faq';

  const [tab, setTab] = useState<AuthTab>('login');
  const [loginMode, setLoginMode] = useState<'password' | 'otp' | 'wecom'>('password');
  const [emailMode, setEmailMode] = useState<EmailMode>('internal');
  const [emailPrefix, setEmailPrefix] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const fullEmail = emailMode === 'internal'
    ? (emailPrefix.trim() ? `${emailPrefix.trim().toLowerCase()}${EMAIL_DOMAIN}` : '')
    : externalEmail.trim().toLowerCase();
  const hasValidEmail = emailMode === 'internal' ? !!emailPrefix.trim() : isValidEmail(fullEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const captchaRef = useRef<CaptchaHandle | null>(null);

  // 企微扫码状态
  const [wecomLoading, setWecomLoading] = useState(false);
  const [wecomFallback, setWecomFallback] = useState<WeComFallback | null>(null);
  const wecomPanelRef = useRef<ReturnType<typeof ww.createWWLoginPanel> | null>(null);

  // OTP 状态
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const otpTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 重置表单
  const resetForm = useCallback(() => {
    setEmailMode('internal');
    setEmailPrefix('');
    setExternalEmail('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowConfirmPassword(false);
    setError('');
    setSuccessMessage('');
    setIsLoading(false);
    setCaptchaValue('');
    setCaptchaError('');
    setWecomFallback(null);
    setOtpSent(false);
    setOtpCode('');
    setOtpCooldown(0);
    if (otpTimerRef.current) { clearInterval(otpTimerRef.current); otpTimerRef.current = null; }
  }, []);

  // 弹窗关闭时重置
  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen, resetForm]);

  // ESC 关闭
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // 密码强度检测
  function getPasswordStrength(pwd: string) {
    if (!pwd) return { level: 0, label: '', color: '' };
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (score <= 2) return { level: 1, label: '弱', color: '#ef4444' };
    if (score <= 3) return { level: 2, label: '中', color: '#f59e0b' };
    return { level: 3, label: '强', color: '#22c55e' };
  }

  const passwordStrength = getPasswordStrength(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  function handleEmailModeChange(mode: EmailMode) {
    setEmailMode(mode);
    setError('');
    setSuccessMessage('');
    setCaptchaError('');
    if (otpSent) {
      setOtpSent(false);
      setOtpCode('');
    }
  }

  function validateEmailInput() {
    if (!fullEmail) {
      setError('请填写邮箱');
      return false;
    }
    if (!hasValidEmail) {
      setError('请输入有效邮箱地址');
      return false;
    }
    return true;
  }

  // 登录
  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!validateEmailInput()) return;
    if (!password.trim()) {
      setError('请填写邮箱和密码');
      return;
    }
    if (!captchaRef.current?.validate(captchaValue)) {
      setCaptchaError('验证码错误');
      return;
    }
    setCaptchaError('');
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    try {
      await signIn(fullEmail, password);
      await fetchProfile();
      onClose();
    } catch {
      setError('邮箱或密码错误');
      captchaRef.current?.regenerate();
      setCaptchaValue('');
    } finally {
      setIsLoading(false);
    }
  }

  // 发送 OTP 验证码
  async function handleSendOtp() {
    if (!validateEmailInput()) return;
    if (!captchaRef.current?.validate(captchaValue)) {
      setCaptchaError('验证码错误');
      return;
    }
    setCaptchaError('');
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    try {
      // 直接调用 supabase，不走 auth-store，避免全局 loading 状态触发页面刷新
      const { error } = await supabase.auth.signInWithOtp({
        email: fullEmail,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: getFaqAuthRedirectUrl(faqBasePath),
          data: { email: fullEmail },
        },
      });
      if (error) throw error;
      setOtpSent(true);
      // 启动 60 秒倒计时
      setOtpCooldown(60);
      otpTimerRef.current = setInterval(() => {
        setOtpCooldown((prev) => {
          if (prev <= 1) {
            if (otpTimerRef.current) clearInterval(otpTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: unknown) {
      console.error('FAQ OTP send failed:', err);
      setError(formatAuthRequestError(err, '发送验证码', emailMode));
      captchaRef.current?.regenerate();
      setCaptchaValue('');
    } finally {
      setIsLoading(false);
    }
  }

  // 验证 OTP 登录
  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    if (!otpCode.trim()) { setError('请输入验证码'); return; }
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    try {
      // 直接调用 supabase verifyOtp，成功后会自动触发 onAuthStateChange → set user
      const { error } = await supabase.auth.verifyOtp({
        email: fullEmail,
        token: otpCode.trim(),
        type: 'email',
      });
      if (error) throw error;
      await fetchProfile();
      onClose();
    } catch {
      setError('验证码错误或已过期');
    } finally {
      setIsLoading(false);
    }
  }

  // 注册
  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    if (!validateEmailInput()) return;
    if (!password.trim()) { setError('请填写密码'); return; }
    if (password.length < 6) { setError('密码至少 6 位'); return; }
    if (password !== confirmPassword) { setError('两次密码不一致'); return; }
    if (!captchaRef.current?.validate(captchaValue)) {
      setCaptchaError('验证码错误');
      return;
    }
    setCaptchaError('');
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: fullEmail,
        password,
        options: {
          emailRedirectTo: getFaqAuthRedirectUrl(faqBasePath),
          data: { email: fullEmail },
        },
      });
      if (signUpError) throw signUpError;
      if (!data.session) {
        setSuccessMessage(`验证邮件已发送至 ${fullEmail}，请打开邮箱完成验证后再登录。`);
        setPassword('');
        setConfirmPassword('');
        setCaptchaValue('');
        captchaRef.current?.regenerate();
        return;
      }
      await fetchProfile();
      onClose();
    } catch (err: unknown) {
      console.error('FAQ register failed:', err);
      setError(formatAuthRequestError(err, '注册', emailMode));
      captchaRef.current?.regenerate();
      setCaptchaValue('');
    } finally {
      setIsLoading(false);
    }
  }

  // ── 企业微信扫码登录 ──
  async function handleWeComLogin(code: string) {
    setWecomLoading(true);
    setError('');
    setWecomFallback(null);
    try {
      const shouldDebugWeCom = localStorage.getItem('wecomLoginDebug') === '1';
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/wecom-login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            debug: shouldDebugWeCom,
            requireRealEmail: true,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (shouldDebugWeCom && data.wecomDebug) {
        console.info('企微扫码登录诊断:', data.wecomDebug);
      }
      if (!res.ok) {
        if (data.code === 'WECOM_EMAIL_NOT_ALLOWED' || data.email_source === 'not_available' || res.status === 403) {
          setWecomFallback({
            userid: data.wecom_userid,
            detail: data.message || WECOM_UNSUPPORTED_DETAIL,
          });
          return;
        }
        throw new Error(data.error || data.message || '企微登录失败');
      }

      // 设置 Supabase session
      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      });
      await fetchProfile();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '企微登录失败，请重试');
    } finally {
      setWecomLoading(false);
    }
  }

  // 初始化企微扫码面板
  function initWeComPanel() {
    setWecomFallback(null);
    // 清理旧面板
    if (wecomPanelRef.current) {
      try { wecomPanelRef.current.unmount?.(); } catch { /* ignore */ }
      wecomPanelRef.current = null;
    }

    const origin = window.location.origin;

    // 防御：本地开发环境不支持企微扫码（redirect_uri 域名校验会失败）
    if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
      setError('企微扫码登录需要在正式域名下使用，本地环境不支持');
      return;
    }

    // 防御：环境变量缺失
    const corpId = import.meta.env.VITE_WECOM_CORPID;
    const agentId = import.meta.env.VITE_WECOM_AGENTID;
    if (!corpId || !agentId) {
      console.error('企微登录配置缺失: VITE_WECOM_CORPID 或 VITE_WECOM_AGENTID 未设置');
      setError('企微登录配置不完整，请联系管理员');
      return;
    }

    // redirect_uri 不要手动 encodeURIComponent —— SDK 内部会自行编码
    // 必须与企微后台配置的「授权回调域」一致
    const redirectUri = origin + faqBasePath;

    // 延迟初始化，确保 DOM 已渲染
    setTimeout(() => {
      const container = document.getElementById('ww_login_container');
      if (!container) return;

      try {
        const panel = ww.createWWLoginPanel({
          el: '#ww_login_container',
          params: {
            login_type: 'CorpApp',
            appid: corpId,
            agentid: agentId,
            redirect_uri: redirectUri,
            state: crypto.randomUUID(),
            redirect_type: 'callback',
            scope: 'snsapi_privateinfo',
          },
          onLoginSuccess({ code }: { code: string }) {
            handleWeComLogin(code);
          },
          onLoginFail(err: unknown) {
            console.error('企微扫码失败:', err);
            setError('扫码未完成或已取消，请重新扫码');
          },
        });
        wecomPanelRef.current = panel;
      } catch (err) {
        console.error('企微 SDK 初始化失败:', err);
        setError('企微登录组件加载失败，请刷新页面重试');
      }
    }, 100);
  }

  // 切换到企微登录模式时初始化面板
  useEffect(() => {
    if (loginMode === 'wecom' && isOpen) {
      initWeComPanel();
    }
    return () => {
      if (wecomPanelRef.current) {
        try { wecomPanelRef.current.unmount?.(); } catch { /* ignore */ }
        wecomPanelRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginMode, isOpen]);

  function renderEmailModeSelector(disabled = false) {
    return (
      <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleEmailModeChange('internal')}
          className={`rounded-lg px-3 py-2 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
            emailMode === 'internal'
              ? 'bg-white text-sky-600 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          内部邮箱
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => handleEmailModeChange('external')}
          className={`rounded-lg px-3 py-2 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
            emailMode === 'external'
              ? 'bg-white text-amber-600 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          外部邮箱
        </button>
      </div>
    );
  }

  function renderEmailField(disabled = false) {
    return (
      <div className={`relative flex items-center bg-slate-50 border border-slate-200 rounded-xl focus-within:ring-2 focus-within:ring-sky-200 focus-within:border-sky-300 transition-all ${disabled ? 'opacity-60' : ''}`}>
        <Mail size={16} className="ml-3.5 text-slate-400 flex-shrink-0" />
        {emailMode === 'internal' ? (
          <>
            <input
              type="text"
              value={emailPrefix}
              onChange={(e) => {
                setEmailPrefix(e.target.value.replace(/@.*$/, ''));
                if (otpSent) { setOtpSent(false); setOtpCode(''); }
              }}
              placeholder="邮箱前缀"
              autoComplete="username"
              required
              disabled={disabled}
              className="flex-1 pl-2 pr-1 py-3 bg-transparent text-sm outline-none min-w-0 disabled:opacity-60"
            />
            <span className="pr-3.5 text-sm text-slate-400 flex-shrink-0 select-none">{EMAIL_DOMAIN}</span>
          </>
        ) : (
          <input
            type="email"
            value={externalEmail}
            onChange={(e) => {
              setExternalEmail(e.target.value);
              if (otpSent) { setOtpSent(false); setOtpCode(''); }
            }}
            placeholder="name@example.com"
            autoComplete="username"
            required
            disabled={disabled}
            className="flex-1 pl-2 pr-3.5 py-3 bg-transparent text-sm outline-none min-w-0 disabled:opacity-60"
          />
        )}
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tab === 'login' ? '登录' : '注册'}
    >
      <div
        className="relative w-full max-w-[400px] mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ animation: 'faqModalIn 0.2s ease-out' }}
      >
        {/* 顶部渐变装饰条 */}
        <div className="h-1 bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500" />

        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors z-10"
          aria-label="关闭"
        >
          <X size={16} />
        </button>

        {/* 标题区 */}
        <div className="pt-7 pb-4 px-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 text-white mb-3">
            {tab === 'login' ? <LogIn size={22} /> : <UserPlus size={22} />}
          </div>
          <h2 className="text-lg font-bold text-slate-800">
            {tab === 'login' ? '登录 FAQ 知识中心' : '注册新账户'}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            {tab === 'login' ? '登录后享受更多功能' : '创建账户以管理 FAQ 内容'}
          </p>
        </div>

        {/* Tab 切换 */}
        <div className="mx-8 mb-5 flex rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => { setTab('login'); setError(''); setSuccessMessage(''); setWecomFallback(null); }}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
              tab === 'login'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <LogIn size={14} className="inline mr-1.5 -mt-0.5" />
            登录
          </button>
          <button
            type="button"
            onClick={() => { setTab('register'); setError(''); setSuccessMessage(''); setWecomFallback(null); }}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
              tab === 'register'
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <UserPlus size={14} className="inline mr-1.5 -mt-0.5" />
            注册
          </button>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mx-8 mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm leading-5 text-red-600">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="mx-8 mb-4 flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* 登录表单 */}
        {tab === 'login' && loginMode === 'password' && (
          <form onSubmit={handleLogin} className="px-8 pb-8 space-y-4">
            {/* 模式切换 */}
            <div className="flex items-center justify-center gap-1 -mt-1 mb-1">
              <button
                type="button"
                onClick={() => { setLoginMode('password'); setError(''); setWecomFallback(null); }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                  loginMode === 'password' ? 'bg-sky-50 text-sky-600' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <Lock size={11} className="inline mr-1 -mt-0.5" />密码登录
              </button>
              <span className="text-slate-300 text-xs">|</span>
              <button
                type="button"
                onClick={() => { setLoginMode('otp'); setError(''); setWecomFallback(null); setOtpSent(false); setOtpCode(''); }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                  loginMode === 'otp' ? 'bg-sky-50 text-sky-600' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <KeyRound size={11} className="inline mr-1 -mt-0.5" />验证码登录
              </button>
            </div>

            {renderEmailModeSelector()}
            {renderEmailField()}

            {/* 密码 */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="密码"
                autoComplete="current-password"
                required
                className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
              />
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {/* 图形验证码 */}
            <div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={captchaValue}
                  onChange={(e) => { setCaptchaValue(e.target.value); setCaptchaError(''); }}
                  placeholder="输入验证码"
                  className="flex-1 pl-4 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
                />
                <Captcha
                  ref={captchaRef}
                  onChange={() => { setCaptchaError(''); setCaptchaValue(''); }}
                />
              </div>
              {captchaError && (
                <p className="mt-1.5 text-xs text-red-500">{captchaError}</p>
              )}
            </div>

            {/* 登录按钮 */}
            <button
              type="submit"
              disabled={isLoading || !hasValidEmail || !password.trim() || !captchaRef.current?.validate(captchaValue)}
              className="w-full py-3 bg-gradient-to-r from-sky-500 to-indigo-500 text-white text-sm font-semibold rounded-xl hover:from-sky-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  登录中...
                </span>
              ) : '登录'}
            </button>
          </form>
        )}

        {/* OTP 验证码登录表单 */}
        {tab === 'login' && loginMode === 'otp' && (
          <form onSubmit={handleVerifyOtp} className="px-8 pb-8 space-y-4">
            {/* 模式切换 */}
            <div className="flex items-center justify-center gap-1 -mt-1 mb-1">
              <button
                type="button"
                onClick={() => { setLoginMode('password'); setError(''); setWecomFallback(null); }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                  loginMode === 'password' ? 'bg-sky-50 text-sky-600' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <Lock size={11} className="inline mr-1 -mt-0.5" />密码登录
              </button>
              <span className="text-slate-300 text-xs">|</span>
              <button
                type="button"
                onClick={() => { setLoginMode('otp'); setError(''); setWecomFallback(null); }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                  loginMode === 'otp' ? 'bg-sky-50 text-sky-600' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <KeyRound size={11} className="inline mr-1 -mt-0.5" />验证码登录
              </button>
            </div>

            {renderEmailModeSelector(otpSent)}
            {renderEmailField(otpSent)}

            {!otpSent ? (
              <>
                {/* 图形验证码 */}
                <div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={captchaValue}
                      onChange={(e) => { setCaptchaValue(e.target.value); setCaptchaError(''); }}
                      placeholder="输入图形验证码"
                      className="flex-1 pl-4 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
                    />
                    <Captcha
                      ref={captchaRef}
                      onChange={() => { setCaptchaError(''); setCaptchaValue(''); }}
                    />
                  </div>
                  {captchaError && (
                    <p className="mt-1.5 text-xs text-red-500">{captchaError}</p>
                  )}
                </div>

                {/* 发送验证码按钮 */}
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={isLoading || !hasValidEmail || !captchaRef.current?.validate(captchaValue)}
                  className="w-full py-3 bg-gradient-to-r from-sky-500 to-indigo-500 text-white text-sm font-semibold rounded-xl hover:from-sky-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <><Loader2 size={16} className="animate-spin" /> 发送中...</>
                  ) : (
                    <><Send size={14} /> 发送验证码到邮箱</>
                  )}
                </button>
              </>
            ) : (
              <>
                {/* 发送成功提示 */}
                <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-600">
                  <CheckCircle2 size={16} className="shrink-0" />
                  <span>验证码已发送至 <strong>{fullEmail}</strong></span>
                </div>

                {/* OTP 输入 */}
                <div className="relative">
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    placeholder="输入 6 位验证码"
                    maxLength={6}
                    autoFocus
                    className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-center tracking-[0.3em] font-mono font-bold outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
                  />
                  <KeyRound size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>

                {/* 重新发送 */}
                <div className="text-center">
                  {otpCooldown > 0 ? (
                    <span className="text-xs text-slate-400">{otpCooldown} 秒后可重新发送</span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      className="text-xs text-sky-500 hover:text-sky-600 font-medium transition-colors"
                    >重新发送验证码</button>
                  )}
                </div>

                {/* 登录按钮 */}
                <button
                  type="submit"
                  disabled={isLoading || otpCode.length !== 6}
                  className="w-full py-3 bg-gradient-to-r from-sky-500 to-indigo-500 text-white text-sm font-semibold rounded-xl hover:from-sky-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      验证中...
                    </span>
                  ) : '登录'}
                </button>
              </>
            )}
          </form>
        )}

        {/* 企业微信扫码登录 */}
        {tab === 'login' && loginMode === 'wecom' && (
          <div className="px-8 pb-8">
            {/* 返回按钮 */}
            <button
              type="button"
              onClick={() => { setLoginMode('password'); setError(''); setWecomFallback(null); }}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors mb-4 -mt-1"
            >
              <ArrowLeft size={13} />
              返回密码登录
            </button>

            {/* 加载/扫码中状态 */}
            {wecomLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Loader2 size={28} className="animate-spin text-sky-500" />
                <p className="text-sm text-slate-500">正在登录，请稍候...</p>
              </div>
            ) : wecomFallback ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-5">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                    <AlertCircle size={18} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-amber-900">{WECOM_UNSUPPORTED_TITLE}</h3>
                    <p className="mt-1 text-xs leading-5 text-amber-700">
                      {wecomFallback.detail || WECOM_UNSUPPORTED_DETAIL}
                    </p>
                    {wecomFallback.userid && (
                      <p className="mt-2 truncate text-[11px] text-amber-600">
                        企业微信 UserID：{wecomFallback.userid}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setLoginMode('password'); setError(''); setWecomFallback(null); }}
                    className="rounded-xl bg-white px-3 py-2.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    密码登录
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTab('register'); setLoginMode('password'); setError(''); setWecomFallback(null); }}
                    className="rounded-xl bg-amber-500 px-3 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-amber-600"
                  >
                    注册账号
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* 扫码区域 */}
                <div
                  id="ww_login_container"
                  className="mx-auto flex items-center justify-center"
                  style={{ width: 300, height: 400 }}
                />
                <p className="text-center text-[11px] text-slate-400 mt-3">
                  仅限FAQ 管理员可扫码登录，其他用户请使用邮箱密码注册、登录
                </p>
              </>
            )}
          </div>
        )}

        {/* 企微登录入口分割线（仅在密码/OTP登录模式时显示） */}
        {tab === 'login' && loginMode !== 'wecom' && (
          <div className="px-8 pb-6">
            {/* 分割线 */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-[11px] text-slate-400 shrink-0">或</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* 企微登录按钮 */}
            <button
              type="button"
              onClick={() => { setLoginMode('wecom'); setError(''); setWecomFallback(null); }}
              className="w-full flex items-center justify-center gap-2.5 py-3 bg-[#f0f5ff] hover:bg-[#e4edff] border border-[#d4e3ff] text-[#1a56db] text-sm font-medium rounded-xl transition-all"
            >
              <Building2 size={17} />
              企业微信扫码登录
            </button>
            <p className="text-center text-[10px] text-slate-400 mt-2">仅限FAQ 管理员可扫码登录，其他用户请使用邮箱密码注册、登录</p>
          </div>
        )}

        {/* 注册表单 */}
        {tab === 'register' && (
          <form onSubmit={handleRegister} className="px-8 pb-8 space-y-4">
            {renderEmailModeSelector()}
            {renderEmailField()}

            {/* 密码 */}
            <div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="密码（至少 6 位）"
                  autoComplete="new-password"
                  required
                  className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
                />
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {/* 密码强度 */}
              {password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${(passwordStrength.level / 3) * 100}%`,
                        backgroundColor: passwordStrength.color,
                      }}
                    />
                  </div>
                  <span className="text-[11px] font-medium" style={{ color: passwordStrength.color }}>
                    {passwordStrength.label}
                  </span>
                </div>
              )}
            </div>

            {/* 确认密码 */}
            <div>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="确认密码"
                  autoComplete="new-password"
                  required
                  className="w-full pl-10 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
                />
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {/* 匹配提示 */}
              {confirmPassword && (
                <div className="mt-2 flex items-center gap-1.5">
                  {passwordsMatch ? (
                    <>
                      <CheckCircle2 size={13} className="text-green-500" />
                      <span className="text-[11px] font-medium text-green-500">密码一致</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle size={13} className="text-red-400" />
                      <span className="text-[11px] font-medium text-red-400">密码不一致</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* 验证码 */}
            <div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={captchaValue}
                  onChange={(e) => { setCaptchaValue(e.target.value); setCaptchaError(''); }}
                  placeholder="输入验证码"
                  className="flex-1 pl-4 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300 transition-all"
                />
                <Captcha
                  ref={captchaRef}
                  onChange={() => { setCaptchaError(''); setCaptchaValue(''); }}
                />
              </div>
              {captchaError && (
                <p className="mt-1.5 text-xs text-red-500">{captchaError}</p>
              )}
            </div>

            {/* 注册按钮 */}
            <button
              type="submit"
              disabled={isLoading || !hasValidEmail || !password.trim() || !passwordsMatch || !captchaRef.current?.validate(captchaValue)}
              className="w-full py-3 bg-gradient-to-r from-sky-500 to-indigo-500 text-white text-sm font-semibold rounded-xl hover:from-sky-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  注册中...
                </span>
              ) : '注册'}
            </button>
          </form>
        )}
      </div>

      {/* 弹窗入场动画 */}
      <style>{`
        @keyframes faqModalIn {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
